import type { TermType } from '@/types';

/**
 * Pure, React/DOM-free helpers for the Trade Terms tab (Phase 10).
 *
 *  - `resolveCreditRating` performs the TERMS-04 FE-join: the counterparty
 *    `credit_rating` is NOT on `PositionDetail`; it is joined on the frontend
 *    from the counterparty registry (`useCounterparties()`), matched by exact
 *    `counterparty_name`. The result is a small discriminated union the
 *    component maps to its loading / present / not-available chip states. It
 *    never throws and never fabricates a rating.
 *
 *  - `resolveMaturityDisplay` encodes the term-type-aware maturity copy from
 *    10-UI-SPEC §"Term & dates" so "Open — no fixed maturity" / "Evergreen —
 *    rolls until notice" never degrades to a bare "—".
 */

/** Minimal row shape consumed by the FE-join (a subset of CounterpartyListRow). */
export interface CreditRatingRow {
  name: string;
  creditRating?: string;
}

export type CreditRatingResult =
  | { state: 'loading' }
  | { state: 'present'; rating: string }
  | { state: 'unavailable' };

/**
 * Join a counterparty name against the registry rows and resolve its credit
 * rating into a three-state result.
 *
 *  - registry query errored        → unavailable (degrade silently, never stick on loading)
 *  - rows === undefined            → loading (registry query in flight)
 *  - exact-name match w/ rating     → present
 *  - exact-name match, no rating    → unavailable
 *  - no match (incl. empty name)    → unavailable
 *
 * Matching is exact (===) — never fuzzy/substring. An empty counterparty name
 * is treated as unavailable so a stray empty registry row can never fabricate
 * a rating.
 *
 * `isError` is checked BEFORE the `rows === undefined` loading branch: a hard
 * TanStack Query error also leaves `data` undefined, and per 10-UI-SPEC
 * §Credit-Rating *error* state the chip must degrade to "not available" rather
 * than pulse forever. Loading is therefore genuinely-still-fetching only.
 */
export function resolveCreditRating(
  counterpartyName: string,
  rows: CreditRatingRow[] | undefined,
  isError = false,
): CreditRatingResult {
  if (isError) {
    return { state: 'unavailable' };
  }
  if (rows === undefined) {
    return { state: 'loading' };
  }
  if (!counterpartyName) {
    return { state: 'unavailable' };
  }
  const match = rows.find((row) => row.name === counterpartyName);
  const rating = match?.creditRating;
  if (rating) {
    return { state: 'present', rating };
  }
  return { state: 'unavailable' };
}

export type MaturityDisplay =
  | { kind: 'open'; label: string }
  | { kind: 'rolling'; label: string }
  | { kind: 'date'; date: string | null };

/**
 * Resolve the term-type-aware Maturity Date copy.
 *
 *  - 'open'                              → "Open — no fixed maturity"
 *  - 'evergreen' & notice NOT served      → "Evergreen — rolls until notice"
 *  - 'evergreen' & notice served          → the resolved maturity date
 *  - 'term' (or undefined)                → the fixed maturity date
 *
 * For the date kind the renderer formats `date` via `formatDate` (which yields
 * "—" for null) — but open/rolling kinds carry explicit copy, never a "—".
 */
export function resolveMaturityDisplay(
  termType: TermType | undefined,
  maturityDate: string | null,
  noticeServed: boolean | undefined,
): MaturityDisplay {
  if (termType === 'open') {
    return { kind: 'open', label: 'Open — no fixed maturity' };
  }
  if (termType === 'evergreen' && !noticeServed) {
    return { kind: 'rolling', label: 'Evergreen — rolls until notice' };
  }
  return { kind: 'date', date: maturityDate };
}
